import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/* ================= BRAND COLORS ================= */
const COLORS = {
  primary: "#0D47A1",
  red: "#D32F2F",
  yellow: "#FBC02D",
  bgCream: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E5E7EB",
  textDark: "#1E1E1E",
  textGrey: "#757575",
  success: "#16A34A",
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    load();

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const load = async () => {
    const res = await api.get("/dashboard/summary.php");
    setData(res.data?.data);
  };

  if (!data) return <div style={{ padding: 20 }}>Loading...</div>;

  const chartData = [
    { name: "Sales", value: data.sales },
    { name: "Purchase", value: data.purchase },
    { name: "Profit", value: data.profit },
  ];

  return (
    <div style={{ ...styles.page, padding: isMobile ? 15 : "10px 30px 30px" }}>
      
      {/* HEADER */}
      <div style={styles.pageHeader}>
        <h2 style={{ ...styles.title, fontSize: isMobile ? 18 : 24 }}>
          Dashboard Overview
        </h2>
        <span style={styles.subtitle}>
          Monitor your business performance in real time
        </span>
      </div>

      {/* QUICK ACTIONS */}
      <div style={styles.gridAuto}>
        <ActionCard
          title="New Sale"
          desc="Create a new sale invoice"
          color={COLORS.success}
          onClick={() => navigate("/sales")}
        />
        <ActionCard
          title="New Purchase"
          desc="Record a new purchase bill"
          color={COLORS.primary}
          onClick={() => navigate("/purchase")}
        />
      </div>

      {/* KPI */}
      <div style={styles.gridAuto}>
        <KPI label="Total Revenue" value={data.sales} color={COLORS.success} />
        <KPI label="Total Expenses" value={data.purchase} color={COLORS.red} />
        <KPI label="Net Profit" value={data.profit} color={COLORS.primary} />
        <KPI
  label="Stock Value"
  value={data.stock_value}
  color={COLORS.primary}
  onClick={() => navigate("/inventory")}
/>
        <KPI
  label="Cash in Hand"
  value={data.cash}
  color={COLORS.yellow}
  onClick={() => navigate("/cash")}
/>
      </div>

      <div style={styles.gridAuto}>
  <KPI
    label="Receivable"
    value={data.receivable}
    color={COLORS.success}
    onClick={() => navigate("/customers?filter=receivable")}
  />

  <KPI
    label="Payable"
    value={data.payable}
    color={COLORS.red}
    onClick={() => navigate("/customers?filter=payable")}
  />
</div>

      {/* ANALYTICS */}
      <div
        style={{
          ...styles.analyticsSection,
          gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
        }}
      >
        <div style={styles.graphCard}>
          <div style={styles.cardHeader}>Business Analytics</div>

          <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="value"
                fill={COLORS.primary}
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.insightTitle}>Business Insight</h3>
          <p style={styles.insightText}>
            Your current profit margin is strong. Focus on optimizing purchase
            costs to further increase net profit.
          </p>
        </div>
      </div>
    </div>
  );
};

/* COMPONENTS */

const ActionCard = ({ title, desc, color, onClick }) => (
  <div
    style={{ ...styles.actionCard, borderTop: `4px solid ${color}` }}
    onClick={onClick}
  >
    <div style={styles.actionTitle}>{title}</div>
    <div style={styles.actionDesc}>{desc}</div>
  </div>
);

const KPI = ({ label, value, color, onClick }) => {
  const safeValue = Number(value ?? 0);

  return (
    <div
      style={{
        ...styles.kpiCard,
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      <div style={styles.kpiLabel}>{label}</div>
      <div style={{ ...styles.kpiValue, color }}>
        ₹ {safeValue.toLocaleString("en-IN")}
      </div>
    </div>
  );
};

/* STYLES */

const styles = {
  page: {
    background: COLORS.bgCream,
    minHeight: "100vh",
  },

  pageHeader: {
    marginBottom: 20,
  },

  title: {
    fontWeight: 700,
    color: COLORS.textDark,
  },

  subtitle: {
    fontSize: 13,
    color: COLORS.textGrey,
  },

  gridAuto: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))",
    gap: 15,
    marginBottom: 20,
  },

  actionCard: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },

  actionTitle: {
    fontSize: 15,
    fontWeight: 600,
  },

  actionDesc: {
    fontSize: 12,
    color: COLORS.textGrey,
  },

  kpiCard: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },

  kpiLabel: {
    fontSize: 12,
    color: COLORS.textGrey,
  },

  kpiValue: {
    fontSize: 18,
    fontWeight: 700,
  },

  analyticsSection: {
    display: "grid",
    gap: 15,
  },

  graphCard: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
  },

  cardHeader: {
    fontWeight: 600,
    marginBottom: 10,
  },

  insightCard: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
  },

  insightTitle: {
    fontSize: 14,
    fontWeight: 600,
  },

  insightText: {
    fontSize: 12,
    color: COLORS.textGrey,
  },
};

export default Dashboard;